import { definedMap } from "@glideapps/ts-necessities";

import {
    getTableColumn,
    ComponentKind,
    ActionKind,
    getPrimitiveColumns,
    LegacyPropertyDescription,
    PropertyKind,
    InputOutputTables,
    TableGlideType,
    TableColumn,
    MutatingScreenKind,
    getTableName,
    PropertyDescription,
    ActionComponentDescription,
    ActionDescription,
    makeEmptyComponentDescription,
} from "../description";
import { AppKind, SerializedComponent } from "../../components/SerializedApp";
import {
    ComponentBuilder,
    ComponentDescriptor,
    EnumPropertyCase,
    ComponentConfiguratorContext,
    PropertyDescriptor,
    ComponentSpecialCaseDescriptor,
    InteractiveComponentConfiguratorContext,
    ActionPropertyDescriptor,
    SchemaInspector,
} from ".";
import { ComponentHandlerBase } from "./handler";
import { makeDefaultActionDescriptor } from "./primitive";
import { defined, definedMapWithDefault, panic } from "../../Support";
import { handlerForActionKind, makeScreenContextForActions } from "../actions";
import {
    getColumnProperty,
    getEnumProperty,
    makeStringProperty,
    makeEnumProperty,
    getIconProperty,
    getActionProperty,
} from "./property-utils";
import { Appearance } from "./button-appearance";
import { PropertySection } from "./property-sections";
import { getDocURL } from "../../docUrl";
import { makeCaptionStringPropertyDescriptor } from "./descriptor-utils";
import { makeText } from "../builder-utils";
import { tokenForProperty } from "../actions/base";
import { Icon } from "../../components/icons";
import { getFeatureSetting } from "../../feature-settings";
import { makeShowToastAction } from "../form-on-submit";
import { Mood, reducedMoodCases } from "./mood";

const ComponentKindButton: ComponentKind = "button";

const defaultFloatingIcon: Icon = "01-47-flash-1";

interface ButtonComponentDescription extends ActionComponentDescription {
    // The form submission button doesn't have a property name
    readonly propertyName: LegacyPropertyDescription | undefined;
    readonly appearance: LegacyPropertyDescription;
    readonly floatingIcon: PropertyDescription | undefined;

    readonly caption: LegacyPropertyDescription | undefined;
    readonly mood: Mood | undefined;
}

function makeAppearanceCases(): readonly EnumPropertyCase<Appearance>[] {
    const cases: EnumPropertyCase<Appearance>[] = [
        {
            value: Appearance.Filled,
            label: "Filled",
            icon: "buttonFilled",
        },
        {
            value: Appearance.Transparent,
            label: "Transparent",
            icon: "buttonTransparent",
        },
        {
            value: Appearance.Bordered,
            label: "Bordered",
            icon: "buttonBordered",
        },
        {
            value: Appearance.Simple,
            label: "Simple",
            icon: "buttonSimple",
        },
    ];
    if (getFeatureSetting("floatingButton")) {
        cases.push({
            value: Appearance.Floating,
            label: "Floating",
            icon: "buttonFloating",
        });
    }
    return cases;
}

function isFormButton({ actions }: ButtonComponentDescription): boolean {
    return getActionProperty(actions)?.kind === ActionKind.FormScreen;
}

function isScanBarcodeButton({ actions }: ButtonComponentDescription): boolean {
    return getActionProperty(actions)?.kind === ActionKind.LiveScanLinearBarcode;
}

function buttonNeedsProperty(desc: ButtonComponentDescription): boolean {
    const action = getActionProperty(desc.actions);
    return action !== undefined && handlerForActionKind(action.kind)?.needsData === true;
}

function getAppearance(desc: ButtonComponentDescription): Appearance | undefined {
    return getEnumProperty(desc.appearance);
}

function getMood(desc: ButtonComponentDescription): Mood | undefined {
    return getEnumProperty(desc.mood);
}

const formButtonSpecialCase: ComponentSpecialCaseDescriptor = {
    name: "Form Button",
    analyticsName: "form-button",
    description: "Open a form screen",
    img: "componentFormButton",
    group: "Buttons",
    isNew: false,
    needsInputContext: true,
    appKinds: AppKind.App,
};
const floatingButtonSpecialCase: ComponentSpecialCaseDescriptor = {
    name: "Floating Button",
    analyticsName: "floating-button",
    description: "Tap to perform an action",
    img: "componentFloatingButton",
    group: "Buttons",
    isNew: false,
    needsInputContext: true,
    appKinds: AppKind.App,
};
const scanBarcodeButtonSpecialCase: ComponentSpecialCaseDescriptor = {
    name: "Scan Barcode Button",
    analyticsName: "scan-linear-barcode-button",
    description: "Tap to scan a barcode",
    // FIXME: Proper icon
    img: "componentButton",
    group: "Buttons",
    needsInputContext: false,
    isPro: true,
    appKinds: AppKind.App,
};

function makeButtonWithAction(caption: string, action: ActionDescription): ButtonComponentDescription {
    return {
        ...makeEmptyComponentDescription(ComponentKindButton),
        propertyName: undefined,
        caption: makeStringProperty(caption),
        appearance: makeEnumProperty(Appearance.Filled),
        floatingIcon: undefined,
        actions: [action],
        mood: undefined,
    };
}

export class ButtonComponentHandler extends ComponentHandlerBase<ButtonComponentDescription> {
    constructor() {
        super(ComponentKindButton);
    }

    public get needsColumns(): boolean {
        return false;
    }

    public entryForTableColumn(
        builder: ComponentBuilder,
        desc: ButtonComponentDescription,
        tables: InputOutputTables,
        mutatingScreenKind: MutatingScreenKind | undefined,
        _validFlag: SerializedComponent | undefined,
        outputContext: SerializedComponent
    ): SerializedComponent {
        const { b } = builder;
        const { input } = tables;
        const needsProperty = buttonNeedsProperty(desc);

        let propertyName: string | undefined;
        let column: TableColumn | undefined;
        if (needsProperty) {
            propertyName = getColumnProperty(desc.propertyName);
            column = definedMap(propertyName, n => getTableColumn(input, n));
            if (propertyName !== undefined && column === undefined) {
                builder.error(`Could not find column ${propertyName} in table ${getTableName(input).name} for button`);
                return b.emptyUI;
            }
        }

        // Can we use `b.constant` here?
        const ctx = definedMapWithDefault(propertyName, b.data({}), n => b.getProperty(n, b.getContext, input));

        const { actions } = this.getActions(desc, tables.input, builder, mutatingScreenKind);
        const { screenContext, lets } = makeScreenContextForActions(builder, actions, tables, mutatingScreenKind);

        let onTap = builder.makeSequenceFromActions(
            actions,
            tables,
            screenContext,
            outputContext,
            mutatingScreenKind,
            false
        );
        if (onTap === undefined) {
            builder.error("No action for button");
            return b.emptyUI;
        }
        onTap = b.bindContextToValue(onTap, ctx);

        const appearance = getAppearance(desc) ?? Appearance.Filled;
        const mood = getMood(desc) ?? Mood.Default;

        let floatingIcon = getIconProperty(desc.floatingIcon);

        // We must have a default icon if floating.
        if (appearance === Appearance.Floating) {
            floatingIcon = floatingIcon ?? defaultFloatingIcon;
        }

        let ui = b.button(makeText(builder, desc.caption, input) ?? "", appearance, mood, floatingIcon, onTap);
        if (needsProperty) {
            ui = b.ifIsNotEmpty(b.getContext, ui);
        }
        return b.let(lets, ui);
    }

    public getDescriptor(
        desc: ButtonComponentDescription | undefined,
        _table: TableGlideType | undefined,
        _ccc: ComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor<ButtonComponentDescription> {
        const properties: PropertyDescriptor[] = [];
        const isForm = desc !== undefined && isFormButton(desc);
        if (desc === undefined || buttonNeedsProperty(desc)) {
            properties.push({
                kind: PropertyKind.Column,
                property: { name: "propertyName" },
                label: "Data",
                required: true,
                editable: true,
                searchable: false,
                isDefaultCaption: true,
                getAllowedColumns: getPrimitiveColumns,
                section: PropertySection.Data,
            });
        }

        properties.push(makeCaptionStringPropertyDescriptor("Go!", true, mutatingScreenKind));

        const isFloating = desc !== undefined && getAppearance(desc) === Appearance.Floating;

        if (!isFloating) {
            properties.push({
                kind: PropertyKind.Enum,
                property: { name: "mood" },
                label: "Mood",
                menuLabel: "Mood",
                cases: reducedMoodCases,
                defaultCaseValue: Mood.Default,
                section: PropertySection.Design,
                visual: "dropdown",
            });
        }

        properties.push({
            kind: PropertyKind.Icon,
            property: { name: "floatingIcon" },
            label: "Icon",
            defaultIcon: isFloating ? defaultFloatingIcon : undefined,
            section: PropertySection.Design,
        });

        properties.push({
            kind: PropertyKind.Enum,
            property: { name: "appearance" },
            label: "Style",
            menuLabel: "Show as",
            cases: makeAppearanceCases(),
            defaultCaseValue: Appearance.Filled,
            section: PropertySection.Design,
            visual: "small-images",
        });

        properties.push(...this.getBasePropertyDescriptors());

        return {
            name: "Button",
            description: "Tap to perform an action",
            img: "componentButton",
            group: "Buttons",
            helpUrl: getDocURL(isForm ? "formButton" : "button"),
            properties,
        };
    }

    public get specialCaseDescriptors(): readonly ComponentSpecialCaseDescriptor[] {
        return [formButtonSpecialCase, floatingButtonSpecialCase, scanBarcodeButtonSpecialCase];
    }

    public getActionDescriptors(
        _desc: ButtonComponentDescription | undefined,
        _table: TableGlideType | undefined,
        schema: SchemaInspector | undefined,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): readonly ActionPropertyDescriptor<ButtonComponentDescription>[] {
        return [makeDefaultActionDescriptor(schema, mutatingScreenKind, () => makeShowToastAction("Yay!"))];
    }

    public newSpecialCaseComponent(
        specialCaseDescriptor: ComponentSpecialCaseDescriptor,
        tables: InputOutputTables,
        usedProperties: ReadonlySet<TableColumn>,
        editedProperties: ReadonlySet<TableColumn>,
        iccc: InteractiveComponentConfiguratorContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ButtonComponentDescription | undefined {
        if (specialCaseDescriptor === formButtonSpecialCase) {
            const action = defined(handlerForActionKind(ActionKind.FormScreen)).newActionDescription(
                tables,
                iccc,
                mutatingScreenKind
            );
            if (action === undefined) return undefined;

            return makeButtonWithAction("Open Form", action);
        } else if (specialCaseDescriptor === floatingButtonSpecialCase) {
            const desc = this.newComponent(tables, usedProperties, editedProperties, iccc, mutatingScreenKind);
            if (desc === undefined) return undefined;
            return { ...desc, appearance: makeEnumProperty(Appearance.Floating) };
        } else if (specialCaseDescriptor === scanBarcodeButtonSpecialCase) {
            const action = defined(handlerForActionKind(ActionKind.LiveScanLinearBarcode)).newActionDescription(
                tables,
                iccc,
                mutatingScreenKind
            );
            if (action === undefined) return undefined;

            return makeButtonWithAction("Scan Barcode", action);
        } else {
            return panic("No such button special case");
        }
    }

    public getDescriptiveName(
        desc: ButtonComponentDescription,
        tables: InputOutputTables,
        ccc: ComponentConfiguratorContext
    ): [string, string] {
        let name: string;
        if (isFormButton(desc)) {
            name = "Form Button";
        } else if (isScanBarcodeButton(desc)) {
            name = "Scan Barcode Button";
        } else if (getAppearance(desc) === Appearance.Floating) {
            name = "Floating Button";
        } else {
            name = "Button";
        }
        const token = tokenForProperty(desc.caption, tables, ccc);
        return [name, token?.value ?? ""];
    }
}
